import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import './AcceptInvitation.css';

function AdminInviteRegister() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchInvitationInfo();
  }, [token]);

  const fetchInvitationInfo = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`myclub/admins/invitation/${token}/`);
      setInvitation(response.data);

      // Pre-fill email from invitation
      setFormData(prev => ({
        ...prev,
        email: response.data.email || ''
      }));

      if (response.data.status !== 'pending') {
        setError(`This invitation has already been ${response.data.status}.`);
      } else if (response.data.is_expired) {
        setError('This invitation has expired. Please request a new one from the club owner.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired invitation.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!formData.username.trim()) {
      setError('Username is required.');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!formData.password) {
      setError('Password is required.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axiosInstance.post('myclub/admins/register/', {
        token,
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      // Store tokens for automatic login
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);

      // Store user profile with is_club flag
      localStorage.setItem('user_profile', JSON.stringify({
        is_club: true,
        club_id: response.data.club_id,
      }));

      // Redirect to club dashboard
      navigate('/myclub/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invitation-page">
        <div className="accept-invitation-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="accept-invitation-page">
        <div className="accept-invitation-card">
          <div className="error-state">
            <h2>Invalid Invitation</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invitation?.status !== 'pending' || invitation?.is_expired) {
    return (
      <div className="accept-invitation-page">
        <div className="accept-invitation-card">
          <div className="error-state">
            <h2>Invitation Unavailable</h2>
            <p>{error || 'This invitation is no longer valid.'}</p>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invitation-page">
      <div className="accept-invitation-card admin-register-card">
        <div className="invitation-header">
          <h1>Create Your Account</h1>
        </div>

        <div className="invitation-content">
          <div className="club-info">
            <h2>{invitation?.club_name}</h2>
            <p className="club-location">
              {invitation?.club_city}, {invitation?.club_state}
            </p>
          </div>

          <p className="invitation-text">
            You've been invited to manage this club on privateIQ. Create your account below.
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="admin-register-form">
            <div className="form-field">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="Choose a username"
                disabled={submitting}
                autoFocus
              />
            </div>

            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="your@email.com"
                disabled={submitting}
              />
            </div>

            <div className="form-field">
              <label>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="At least 8 characters"
                disabled={submitting}
              />
            </div>

            <div className="form-field">
              <label>Confirm Password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Confirm your password"
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              className="btn-primary submit-btn"
              disabled={submitting}
            >
              {submitting ? 'Creating Account...' : 'Create Account & Join Club'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminInviteRegister;
